import { Box, Tab, Tabs, Typography } from '@mui/material'
import React, { useEffect, useState, useRef } from 'react'
import { useFormik } from 'formik';
import * as Yup from "yup";
import { uploadLogoAction } from '../../redux/actions';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { createWorkspaceWidgetDataAction, getWorkspaceWidgetDataAction, SetupAgencyAction, updateWorkspaceWidgetDataAction, workspaceDetailsAction, workspaceTestEmailAction, workspaceUpdateAction, workspaceValidateSMTPAction, workspaceVerifyDomainAction } from '../../redux/actions/AgencyAction';
import { getConfigurationAction } from '../../redux/actions/HelperActions';
import { showSnackbar } from '../../redux/slices/SnackbarSlice';
import './AgencyReportTable.scss';
import { API_BASE_URL } from '../../axios/axiosInstance';
import AgencySettings from './components/AgencySettings';
import AgencyWidget from './components/AgencyWidget';
import AgencyWidgetDrawer from './components/AgencyWidgetDrawer';
import AgencyAccount from './components/AgencyAccount';
import ConfirmationMsgModal from '../../components/Modals/ConfirmationMsgModal';



// STYLES & CONSTANTS

const styles = {
    typography: {
        fontFamily: 'var(--font-family-manrope)',
        fontWeight: 400,
        fontSize: '16px'
    },
    sectionTitle: {
        fontFamily: 'var(--font-family-rubik)',
        fontSize: '24px',
        fontWeight: 600,
        color: '#111827',
        letterSpacing: '0.00938em',
        mb: 0.5
    },
    pageTitle: {
        fontFamily: 'var(--font-family-rubik)',
        fontSize: { xs: '28px', md: '32px' },
        fontWeight: 700,
        color: '#111827',
        letterSpacing: '-0.02em'
    },
    fieldLabel: {
        fontFamily: 'var(--font-family-manrope)',
        fontWeight: 500,
        fontSize: '15px',
        color: '#374151'
    },
    sectionContainer: {
        my: 3,
        // pt: 3,
        // borderTop: '1px solid #e5e7eb'
    },
    fieldWrapper: {
        width: { xs: "100%", md: "60%" }
    },
    actionButton: {
        textTransform: 'none',
        height: '35px',
        fontSize: '14px',
        fontFamily: 'var(--font-family-manrope)',
        fontWeight: 500,
        padding: '6px 20px',
        borderRadius: 'var(--action-button-radius)',
        boxShadow: 'none',
        '&:hover': {
            boxShadow: 'none'
        }
    }
};

// VALIDATION SCHEMA

const getAgencyValidationSchema = (isedit: boolean) => Yup.object().shape({
    name: Yup.string(),
    logoUrl: Yup.string().nullable(),
    smtp: Yup.object().shape({
        host: Yup.string(),
        port: Yup.number()
            .min(0, "Port must be a non-negative number")
            .integer("Port must be an integer"),
        user: Yup.string()
            .email("Please enter a valid email address"),
        pass: Yup.string()
    }),
    customDomain: Yup.string(),
    privacyPolicyUrl: Yup.string().nullable(),
    termsOfConditionUrl: Yup.string().nullable(),
    setup_call_link: Yup.string().nullable(),
    setup_call_header: Yup.string().nullable(),
    setup_call_description: Yup.string().nullable()
});

// MAIN COMPONENT

const Agency = () => {
    // STATE MANAGEMENT
    const [logoUploading, setLogoUploading] = useState<boolean>(false);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const [testMail, setTestMail] = useState<string>('');
    const [testMailAttempted, setTestMailAttempted] = useState<boolean>(false);

    const [value, setValue] = useState<number>(0);
    const [showSmtpErrorModal, setShowSmtpErrorModal] = useState(false);
    const [domainError, setDomainError] = useState<string>("");

    // Widget Wizard State
    const [widgetStep, setWidgetStep] = useState<number>(0);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const { agency, isedit, workspaceWidgetData, workspaceWidgetDataLoading } = useSelector((state: RootState) => state.agency);
    const { configurationData } = useSelector((state: RootState) => state.helper);
    const config = React.useMemo(() => (Array.isArray(configurationData) ? configurationData[0] : configurationData), [configurationData]);

    const [widgetConfig, setWidgetConfig] = useState<any>({
        widget_name: "Scan Widget – Homepage",
        grid_multiplier: 9,
        radius: 10,
        widget_width: 100,
        font_family: workspaceWidgetData?.data?.widget?.font_family || 'inherit',
        font_size: 16,
        widget_background_color: workspaceWidgetData?.data?.widget?.widget_background_color || '#FFFFFF',
        text_input_border_color: workspaceWidgetData?.data?.widget?.text_input_border_color || '#D1D5DB',
        title_text_color: workspaceWidgetData?.data?.widget?.title_text_color || '#111827',
        back_button_action_color: workspaceWidgetData?.data?.widget?.back_button_action_color || '#2563eb',
        text_input_text_color: workspaceWidgetData?.data?.widget?.text_input_text_color || '#000000',
        text_input_background_color: workspaceWidgetData?.data?.widget?.text_input_background_color || '#ffffff',
        button_text: workspaceWidgetData?.data?.widget?.button_text || 'Submit',
        continue_button_text: workspaceWidgetData?.data?.widget?.continue_button_text || 'Continue',
        submit_button_text_color: workspaceWidgetData?.data?.widget?.submit_button_text_color || '#FFFFFF',
        submit_button_background_color: workspaceWidgetData?.data?.widget?.submit_button_background_color || '#2970FF',
        continue_button_text_color: workspaceWidgetData?.data?.widget?.continue_button_text_color || '#FFFFFF',
        continue_button_background_color: workspaceWidgetData?.data?.widget?.continue_button_background_color || '#2970FF',
        contact_fields: workspaceWidgetData?.data?.widget?.contact_fields || {
            firstName: { enabled: true, required: 'Required' },
            lastName: { enabled: true, required: 'Required' },
            phoneNumber: { enabled: true, required: 'Required' },
            email: { enabled: true, required: 'Required' },
            website: { enabled: true, required: 'Optional' },
            companyName: { enabled: false, required: 'Optional' },
            businessName: { enabled: false, required: 'Optional' }
        },
        notify_user_with_report: workspaceWidgetData?.data?.widget?.notify_user_with_report ?? true,
        notify_on_new_report: workspaceWidgetData?.data?.widget?.notify_on_new_report ?? true,
        email_template: workspaceWidgetData?.data?.widget?.email_template || 'Hi {{firstName}},\n\nYour report for {{businessName}} is ready.\n\n{{report_link}}\n\n{{agencyName}}',
        success_message: workspaceWidgetData?.data?.widget?.success_message || "Thank you! We will be in touch with you soon.",
        insufficient_credits_message: workspaceWidgetData?.data?.widget?.insufficient_credits_message || "A scan was submitted but could not be generated due to insufficient credits.",
        duplicate_scan_message: workspaceWidgetData?.data?.widget?.duplicate_scan_message || "You have already submitted a scan. Contact us at support@youragency.com.",
        active: workspaceWidgetData?.data?.widget?.active ?? true,
        crm_enable: workspaceWidgetData?.data?.widget?.crm_enable ?? false,
        go_high_level_api_key: workspaceWidgetData?.data?.widget?.go_high_level_api_key || '',
        go_high_level_location_id: workspaceWidgetData?.data?.widget?.go_high_level_location_id || ''
    });
    const [copied, setCopied] = useState<boolean>(false);
    const codeRef = useRef<HTMLPreElement>(null);

    const handleCopy = async () => {
        const textToCopy = codeRef?.current ? codeRef?.current?.innerText : '';
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator?.clipboard?.writeText(textToCopy);
                setCopied(true);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = textToCopy;
                textarea.setAttribute('readonly', '');
                textarea.style.position = 'absolute';
                textarea.style.left = '-9999px';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
                setCopied(true);
            }
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Copy failed', err);
        }
    };

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    const dispatch = useDispatch<AppDispatch>();

    // FORMIK CONFIGURATION
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: agency?.name || "",
            logoUrl: agency?.logoUrl || null,
            smtp: {
                host: agency?.smtp?.host ?? "",
                port: agency?.smtp?.port ?? 0,
                user: agency?.smtp?.user ?? "",
                pass: agency?.smtp?.pass ?? "",
            },
            customDomain: agency?.customDomain || "",
            privacyPolicyUrl: agency?.privacyPolicyUrl || "",
            termsOfConditionUrl: agency?.termsOfConditionUrl || "",
            setup_call_link: agency?.setup_call_link || "",
            setup_call_header: agency?.setup_call_header || "",
            setup_call_description: agency?.setup_call_description || ""
        },
        validationSchema: getAgencyValidationSchema(isedit),
        onSubmit: (values) => {
            const payload = {
                name: values.name || "",
                logoUrl: values.logoUrl || "",
                customDomain: values.customDomain || "",
                privacyPolicyUrl: values.privacyPolicyUrl || null,
                termsOfConditionUrl: values.termsOfConditionUrl || null,
                setup_call_link: values.setup_call_link || null,
                setup_call_header: values.setup_call_header || null,
                setup_call_description: values.setup_call_description || null,
                smtp: {
                    host: values.smtp.host || "",
                    port: Number(values.smtp.port) || 0,
                    user: values.smtp.user || "",
                    pass: values.smtp.pass || ""
                }
            };

            if (isedit) {
                // dispatch(UpdateAgencyAction(payload)).unwrap().then((res) => {
                dispatch(workspaceUpdateAction(payload)).unwrap().then((res: any) => {
                    if (res.success) {
                        // dispatch(getAgencyAction());
                        dispatch(workspaceDetailsAction());
                    }
                }).catch((e: any) => console.error(e));
            } else {
                dispatch(SetupAgencyAction(payload)).unwrap().then((res: any) => {
                    if (res.success) {
                        // dispatch(getAgencyAction());
                        dispatch(workspaceDetailsAction());
                    }
                }).catch((e: any) => console.error(e));
            }
        }
    });

    useEffect(() => {
        // dispatch(getAgencyAction());
        dispatch(workspaceDetailsAction());
        dispatch(getWorkspaceWidgetDataAction());
        dispatch(getConfigurationAction());
    }, []);

    // Auto-save default widget config when user opens the Widget tab for the first time
    useEffect(() => {
        if (value === 1 && !workspaceWidgetData?.data?.widget) {
            dispatch(createWorkspaceWidgetDataAction(widgetConfig)).unwrap().then(() => {
                dispatch(getWorkspaceWidgetDataAction());
            }).catch((err: any) => console.error('Auto-create widget failed:', err));
        }
    }, [value]);

    useEffect(() => {
        if (workspaceWidgetData?.data?.widget) {
            setWidgetConfig((prev: any) => ({
                ...prev,
                ...workspaceWidgetData.data.widget
            }));
        }
    }, [workspaceWidgetData]);

    useEffect(() => {
        if (agency) {
            setLogoPreview(agency?.logoUrl);
        }
    }, [agency]);

    // EVENT HANDLERS
    const handleTogglePassword = () => setShowPassword((prev) => !prev);

    const handleReset = () => {
        formik?.resetForm();
        setLogoPreview(null);
    };

    const handleLogoChange = async (files: File[]) => {
        const file = files?.[0];
        if (!file) return;

        const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif"];
        const allowedExtensions = ["jpg", "jpeg", "png", "webp", "heic", "heif"];
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        const isValidType = allowedTypes.includes(file.type);
        const isValidExtension = fileExtension && allowedExtensions.includes(fileExtension);

        if (!isValidType && !isValidExtension) {
            formik.setFieldError("logoUrl", "Only JPG, JPEG, PNG, WEBP, HEIC, and HEIF files are allowed");
            formik.setFieldTouched("logoUrl", true);
            return;
        }

        setLogoUploading(true);

        try {
            const { processHeicFile } = await import('../../utils/heicConverter');
            const processedFile = await processHeicFile(file);
            const formData = new FormData();
            formData.append("file", processedFile);

            const result: any = await dispatch(uploadLogoAction(formData));

            if (uploadLogoAction.fulfilled.match(result)) {
                const uploadedUrl = result.payload?.url;
                formik.setFieldValue("logoUrl", uploadedUrl);
                setLogoPreview(uploadedUrl);
            } else {
                formik.setFieldError("logoUrl", "Failed to upload image");
                formik.setFieldTouched("logoUrl", true);
            }
        } catch (error) {
            console.error('Error processing logo file:', error);
            formik.setFieldError("logoUrl", "Upload error occurred");
            formik.setFieldTouched("logoUrl", true);
        }

        setLogoUploading(false);
    };

    const handleDeleteLogo = () => {
        formik.setFieldValue("logoUrl", null);
        setLogoPreview(null);
    };

    const handleVerifySMTP = async () => {
        const { host, port, user, pass } = formik.values.smtp;
        if (!host || !user || !pass || !port) {
            setShowSmtpErrorModal(true);
            return;
        }

        formik.setFieldTouched("smtp.pass", true);
        const errors: any = await formik.validateForm();
        if (errors?.smtp?.pass) {
            return;
        }

        const payload = { host, port, user, pass };

        dispatch(workspaceValidateSMTPAction(payload)).unwrap().then((res: any) => {
            dispatch(workspaceDetailsAction());
        }).catch((err: any) => {
            console.error(err);
        });
    };

    const handleVerifyDomain = async () => {
        setDomainError("");
        const domain = formik.values.customDomain?.trim();

        if (!domain) {
            setDomainError("Domain is required");
            return;
        }

        const domainRegex = /^([a-z0-9-]+\.)+[a-z]{2,}$/i;
        if (!domainRegex.test(domain)) {
            setDomainError("Invalid domain format");
            return;
        }

        // dispatch(VerifyDomainAction(domain)).unwrap().then((res) => {
        dispatch(workspaceVerifyDomainAction(domain)).unwrap().then((res: any) => {
            if (res.success) {
                // dispatch(getAgencyAction());
                dispatch(workspaceDetailsAction());
            }
        }).catch((e: any) => console.error(e));
    };

    const handleCopyCNAME = () => {
        navigator.clipboard.writeText("dashboard.mapranking.com");
        dispatch(showSnackbar({ message: "Copied to clipboard", type: "success" }));
    };

    const handleTestMail = () => {
        setTestMailAttempted(true);
        if (testMail === "") {
            return;
        }
        // dispatch(testSMTPAction(testMail)).unwrap().catch((err) => {
        dispatch(workspaceTestEmailAction(testMail)).unwrap().catch((err: any) => {
            console.error(err);
        });
    };

    const handleSaveWidgetConfig = () => {
        if (workspaceWidgetData?.data?.widget) {
            dispatch(updateWorkspaceWidgetDataAction(widgetConfig)).unwrap().then(() => {
                dispatch(getWorkspaceWidgetDataAction());
            }).catch((err: any) => console.error("Update failed:", err));
        } else {
            dispatch(createWorkspaceWidgetDataAction(widgetConfig)).unwrap().then(() => {
                dispatch(getWorkspaceWidgetDataAction());
            }).catch((err: any) => console.error("Create failed:", err));
        }
    };

    return (
        <Box
            sx={{
                width: '100%',
                minHeight: '100vh',
                px: { xs: 2, sm: 3, md: 5 },
                py: { xs: 3, md: 4 },
            }}
        >

            <Box sx={{ mb: 4 }}>
                <Typography sx={styles.pageTitle}>
                    White Label Settings
                </Typography>
                <Typography
                    sx={{
                        ...styles.typography,
                        color: '#6b7280',
                        mt: 0.5
                    }}
                >
                    Customize your workspace appearance, email configuration, and domain settings.
                </Typography>
            </Box>

            <Tabs value={value} onChange={handleChange} variant="scrollable" scrollButtons={false} aria-label="settings tabs" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tab
                    label="Settings"
                    sx={{
                        fontFamily: ' var(--font-family-manrope)',
                        fontSize: 15,
                        fontWeight: 500,
                        color: '#374151',
                        transition: 'color 0.25s ease',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        py: 0.6,
                        textTransform: 'capitalize'
                    }}
                />
                <Tab
                    label="Ranking Report Widget"
                    sx={{
                        fontFamily: 'var(--font-family-manrope)',
                        fontSize: 15,
                        fontWeight: 500,
                        color: '#374151',
                        transition: 'color 0.25s ease',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        py: 0.6,
                        textTransform: 'capitalize'
                    }}
                />
                <Tab
                    label="Account"
                    sx={{
                        fontFamily: 'var(--font-family-manrope)',
                        fontSize: 15,
                        fontWeight: 500,
                        color: '#374151',
                        transition: 'color 0.25s ease',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        py: 0.6,
                        textTransform: 'capitalize'
                    }}
                />
            </Tabs>

            <Box
                sx={{
                    maxWidth: "1200px",
                    backgroundColor: '#ffffff',
                    borderRadius: 'var(--medium-border-radius, 12px)',
                    // p: { xs: 3, md: 4 }
                }}
            >
                {value === 0 && (
                    <AgencySettings
                        formik={formik}
                        styles={styles}
                        logoPreview={logoPreview}
                        handleDeleteLogo={handleDeleteLogo}
                        handleLogoChange={handleLogoChange}
                        logoUploading={logoUploading}
                        handleVerifySMTP={handleVerifySMTP}
                        showPassword={showPassword}
                        handleTogglePassword={handleTogglePassword}
                        isedit={isedit}
                        agency={agency}
                        testMail={testMail}
                        setTestMail={setTestMail}
                        testMailAttempted={testMailAttempted}
                        setTestMailAttempted={setTestMailAttempted}
                        handleTestMail={handleTestMail}
                        handleVerifyDomain={handleVerifyDomain}
                        handleCopyCNAME={handleCopyCNAME}
                        handleReset={handleReset}
                        domainError={domainError}
                        setDomainError={setDomainError}
                    />
                )}


                {value === 1 && (
                    <AgencyWidget
                        styles={styles}
                        widgetConfig={widgetConfig}
                        widgetStep={widgetStep}
                        setWidgetStep={setWidgetStep}
                        setIsDrawerOpen={setIsDrawerOpen}
                        handleCopy={handleCopy}
                        copied={copied}
                        codeRef={codeRef}
                        API_BASE_URL={API_BASE_URL}
                        workspaceWidgetData={workspaceWidgetData}
                        widgetLoading={workspaceWidgetDataLoading}
                    />
                )}

                {value === 2 && (
                    <AgencyAccount styles={styles} />
                )}
            </Box >

            {/* Widget Edit Drawer */}
            <AgencyWidgetDrawer
                isDrawerOpen={isDrawerOpen}
                setIsDrawerOpen={setIsDrawerOpen}
                widgetConfig={widgetConfig}
                setWidgetConfig={setWidgetConfig}
                handleSaveWidgetConfig={handleSaveWidgetConfig}
                config={config}
            />
            {/* SMTP Details Required Modal */}
            <ConfirmationMsgModal
                open={showSmtpErrorModal}
                handleClose={() => setShowSmtpErrorModal(false)}
                onConfirm={() => setShowSmtpErrorModal(false)}
                title="Missing SMTP Details"
                msg="Please enter all SMTP details - Host, Port, Email Address, and Password before verifying."
                confirmButtonText="Ok"
            />
        </Box >
    );
};

export default Agency;